import * as React from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { cn } from '../../styles/lib/utils';
import { formatFileSize } from '../../utils/formatFileSize';
import { Text } from '../Text';
import { FileDeleteDialog } from './FileDeleteDialog';
import { FileItem, type FileItemData } from './FileItem';

export type FileUploadProps = {
  /**
   * Array of files currently displayed
   */
  files?: FileItemData[];
  /**
   * Callback when files are selected/uploaded
   */
  onFilesSelected?: (files: File[]) => void;
  /**
   * Callback when a file is downloaded
   */
  onFileDownload?: (file: FileItemData) => void;
  /**
   * Callback when a file is deleted
   */
  onFileDelete?: (file: FileItemData) => void;
  /**
   * Callback when one or more files are rejected by the dropzone
   * (e.g. wrong type, too large, exceeds maxFiles). Useful for surfacing
   * a toast/notification to the user. Defaults to logging a warning.
   */
  onFileRejection?: (rejections: FileRejection[]) => void;
  /**
   * Whether a selection containing rejected files still forwards the accepted ones to
   * `onFilesSelected`. Off by default: a rejection discards the whole selection.
   * @default false
   */
  keepAcceptedOnRejection?: boolean;
  /**
   * Maximum number of files allowed
   * @default 5
   */
  maxFiles?: number;
  /**
   * Maximum file size in bytes
   * @default 100 * 1024 * 1024 * 1024 * 1024 (100 PB - matching Figma, though likely a typo)
   */
  maxFileSize?: number;
  /**
   * Accepted file types (MIME types or extensions)
   * @example ['.png', '.jpg', '.pdf', 'image/*']
   */
  accept?: string[];
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Title text for the section
   * @default "Files"
   */
  title?: string;
  /**
   * Custom upload instruction text
   */
  uploadText?: string;
  /**
   * Whether the user can download files
   * @default false
   */
  canDownload?: boolean;
  /**
   * Whether the user can delete files
   * @default false
   */
  canDelete?: boolean;
  /**
   * Test id applied to the dropzone root, so consumers can target this interactive control.
   */
  'data-testid'?: string;
};

/**
 * FileUpload component provides drag-and-drop file upload functionality
 * with file list display, following the Figma design specifications.
 */
function FileUpload({
  files = [],
  onFilesSelected,
  onFileDownload,
  onFileDelete,
  onFileRejection,
  keepAcceptedOnRejection = false,
  maxFiles = 0,
  maxFileSize = 1024 * 1024 * 1024, // 1 GB
  accept,
  disabled = false,
  title = 'Files',
  uploadText,
  canDownload = false,
  canDelete = false,
  className,
  'data-testid': dataTestId,
}: FileUploadProps) {
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [fileToDelete, setFileToDelete] = React.useState<FileItemData | null>(null);

  const onDrop = React.useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (disabled) return;

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        if (onFileRejection) {
          onFileRejection(rejectedFiles);
        } else {
          rejectedFiles.forEach(({ file, errors }) => {
            errors.forEach((error) => {
              console.warn(`File ${file.name} was rejected:`, error.message);
            });
          });
        }
        if (!keepAcceptedOnRejection) return;
      }

      // Limit to maxFiles
      const filesToAdd = maxFiles ? acceptedFiles.slice(0, maxFiles - files.length) : acceptedFiles;
      if (filesToAdd.length > 0 && onFilesSelected) {
        onFilesSelected(filesToAdd);
      }
    },
    [disabled, maxFiles, files.length, onFilesSelected, onFileRejection, keepAcceptedOnRejection],
  );

  const dropzoneAccept = React.useMemo<Record<string, string[]> | undefined>(() => {
    if (!accept || accept.length === 0) return undefined;
    const result: Record<string, string[]> = {};
    for (const item of accept) {
      if (item.includes('/')) {
        result[item] = result[item] ?? [];
      } else {
        // react-dropzone v15 requires the leading-dot form.
        const ext = item.startsWith('.') ? item : `.${item}`;
        result['application/octet-stream'] = result['application/octet-stream'] ?? [];
        result['application/octet-stream'].push(ext);
      }
    }
    return result;
  }, [accept]);

  const {
    getRootProps,
    getInputProps,
    isDragActive: dropzoneIsDragActive,
    open,
  } = useDropzone({
    onDrop,
    accept: dropzoneAccept,
    maxSize: maxFileSize,
    maxFiles: maxFiles ? maxFiles - files.length : undefined,
    disabled,
    noClick: true, // Disable default click behavior - we'll handle clicks manually
  });

  React.useEffect(() => {
    setIsDragActive(dropzoneIsDragActive);
  }, [dropzoneIsDragActive]);

  const handleDeleteClick = React.useCallback((file: FileItemData) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = React.useCallback(
    (file: FileItemData) => {
      if (onFileDelete) {
        onFileDelete(file);
      }
      setFileToDelete(null);
    },
    [onFileDelete],
  );

  // Get root props but exclude onClick - we only want drag events on the outer container
  const { onClick, ...rootProps } = getRootProps();

  return (
    <div
      {...rootProps}
      data-testid={dataTestId}
      className={cn(
        '@container flex flex-col items-start w-full transition-colors border border-grey-30 rounded-sm p-3 gap-3 focus-ring',
        disabled && 'opacity-50 cursor-not-allowed',
        isDragActive && 'relative',
        className,
      )}
    >
      <input {...getInputProps()} />

      {/* Drag active overlay */}
      {isDragActive && (
        <div className="absolute inset-0 bg-blue-50/70 z-10">
          <div className="absolute inset-3 z-11 flex items-center justify-center rounded-sm border-2 border-dashed border-white bg-white/17">
            <Text className="text-white text-base font-semibold">Drop files here to upload!</Text>
          </div>
        </div>
      )}
      <Text className="text-base font-semibold text-primary">{title}</Text>
      <div
        onClick={disabled ? undefined : open}
        className={cn(
          'border border-dashed rounded-[2px] min-h-[33px] w-full p-2 flex items-center justify-center transition-colors border-grey-30 bg-transparent',
          disabled ? 'cursor-not-allowed' : isDragActive ? '' : 'hover:border-grey-60 cursor-pointer',
        )}
      >
        <Text className="text-xs font-medium text-grey-70 text-center">
          {uploadText ? (
            uploadText
          ) : (
            <>
              <span>Drag files to section or&nbsp;</span>
              <span className="underline">click to select</span>
              {maxFiles ? (
                <span>
                  &nbsp;up to {maxFiles} files a maximum of {formatFileSize(maxFileSize)} each.
                </span>
              ) : (
                <span>&nbsp;files up to {formatFileSize(maxFileSize)} each.</span>
              )}
            </>
          )}
        </Text>
      </div>

      {files.length > 0 && (
        <div className="w-full grid grid-cols-1 gap-3 @[650px]:grid-cols-2">
          {files.map((file) => (
            <FileItem
              key={file.id}
              file={file}
              onDownload={onFileDownload}
              onDelete={handleDeleteClick}
              canDownload={canDownload}
              canDelete={canDelete}
              className="min-w-[300px]"
            />
          ))}
        </div>
      )}

      <FileDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        file={fileToDelete}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

export type { FileRejection };
export { FileUpload };
